import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { v4 } from 'uuid';
import Button from '@prism/button';
import { InputGroupAddon } from '@prism/inputgroup';
import { columnPropType, useCol } from '@prism/col';
import { isPossiblePhoneNumber, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';
import { FormFeedback } from '@prism/form';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var warned = {};
var warnOnce = function warnOnce(message) {
  if (!warned[message]) {
    /* istanbul ignore else */
    if (typeof console !== 'undefined') {
      console.error(message); // eslint-disable-line no-console
    }

    warned[message] = true;
  }
};
var deprecated = function deprecated(propType, explanation) {
  return function (props, propName, componentName) {
    if (props[propName] !== null && typeof props[propName] !== 'undefined') {
      warnOnce("\"".concat(propName, "\" property of \"").concat(componentName, "\" has been deprecated.\n").concat(explanation));
    }

    for (var _len = arguments.length, rest = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
      rest[_key - 3] = arguments[_key];
    }

    return propType.apply(void 0, [props, propName, componentName].concat(rest));
  };
}; // In NODE_ENV=production the Transition.propTypes are wrapped which results in an

var _excluded$6 = ["state", "static", "addon", "bsSize", "className", "innerRef", "invalid", "plaintext", "readonly", "size", "tag", "valid"];

var useInput = function useInput(props) {
  var _classNames;

  props.state;
      props.static;
      props.addon;
      var bsSize = props.bsSize,
      className = props.className,
      innerRef = props.innerRef,
      invalid = props.invalid,
      plaintext = props.plaintext,
      readonly = props.readonly,
      size = props.size,
      tag = props.tag,
      valid = props.valid,
      attributes = _objectWithoutProperties(props, _excluded$6);

  var children = attributes.children,
      type = attributes.type;
  var computedTag;

  if (type === 'select' || type === 'textarea') {
    computedTag = type;
    delete attributes.type;
  } else if (plaintext) {
    computedTag = 'p';
    delete attributes.type;
  } else {
    computedTag = tag || 'input';

    if (computedTag === 'input' && children) {
      warnOnce('Prism `Input`s that render as html `input`s cannot have children. Please use the `tag` prop to define a different tag, or use the `value` or `defaultValue` attribute to specify the value.');
      delete attributes.children;
    }
  }

  var computedClassName = classNames((_classNames = {}, _defineProperty(_classNames, className, className), _defineProperty(_classNames, 'form-control', true), _defineProperty(_classNames, 'is-invalid', invalid), _defineProperty(_classNames, 'is-valid', valid), _defineProperty(_classNames, 'is-readonly', readonly), _defineProperty(_classNames, "form-control-".concat(size), size), _defineProperty(_classNames, "form-control-".concat(bsSize), bsSize), _defineProperty(_classNames, 'form-check-input', type === 'radio' || type === 'checkbox'), _defineProperty(_classNames, 'form-control-plaintext', plaintext), _defineProperty(_classNames, 'form-control-file', type === 'file'), _classNames));
  return _objectSpread2({
    tag: computedTag,
    className: computedClassName,
    readOnly: readonly,
    ref: innerRef
  }, attributes);
};

var _excluded$5 = ["tag"];

var Input = function Input(props) {
  var _useInput = useInput(props),
      Tag = _useInput.tag,
      attributes = _objectWithoutProperties(_useInput, _excluded$5);

  return /*#__PURE__*/React.createElement(Tag, attributes);
};

Input.propTypes = {
  state: deprecated(PropTypes.string, 'Please use the props "valid" and "invalid" to indicate the state.'),
  static: deprecated(PropTypes.bool, 'Please use the prop "plaintext"'),
  addon: PropTypes.bool,
  bsSize: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.string]),
  invalid: PropTypes.bool,
  plaintext: PropTypes.bool,
  size: PropTypes.string,
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  type: PropTypes.string,
  valid: PropTypes.bool,
  readonly: PropTypes.bool
};
Input.defaultProps = {
  state: undefined,
  // deprecated
  static: undefined,
  // deprecated
  addon: false,
  bsSize: '',
  children: null,
  className: '',
  innerRef: null,
  invalid: undefined,
  plaintext: false,
  size: '',
  tag: 'input',
  type: 'text',
  valid: undefined,
  readonly: false
};

var _excluded$4 = ["className", "hidden", "tag", "check", "fontSize", "for"],
    _excluded2 = ["className"];

var Label = function Label(_ref) {
  var _classNames;

  var className = _ref.className,
      hidden = _ref.hidden,
      Tag = _ref.tag,
      check = _ref.check,
      fontSize = _ref.fontSize,
      htmlFor = _ref.for,
      props = _objectWithoutProperties(_ref, _excluded$4);

  var _useCol = useCol(props),
      colClasses = _useCol.className,
      attributes = _objectWithoutProperties(_useCol, _excluded2);

  var computedClassName = classNames((_classNames = {}, _defineProperty(_classNames, className, className), _defineProperty(_classNames, 'col-form-label', true), _defineProperty(_classNames, 'sr-only', hidden), _defineProperty(_classNames, 'form-check-label', check), _defineProperty(_classNames, colClasses, colClasses), _defineProperty(_classNames, "font-size-".concat(fontSize), fontSize), _classNames));
  return /*#__PURE__*/React.createElement(Tag, _extends({
    htmlFor: htmlFor
  }, attributes, {
    className: computedClassName
  }));
};

Label.propTypes = {
  check: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  fontSize: PropTypes.string,
  for: PropTypes.string,
  hidden: PropTypes.bool,
  size: PropTypes.string,
  tag: PropTypes.string,
  widths: PropTypes.arrayOf(columnPropType),
  xs: columnPropType,
  sm: columnPropType,
  md: columnPropType,
  lg: columnPropType,
  xl: columnPropType
};
Label.defaultProps = {
  check: false,
  children: null,
  className: '',
  fontSize: '',
  for: undefined,
  hidden: false,
  size: '',
  tag: 'label',
  widths: ['xs', 'sm', 'md', 'lg', 'xl'],
  xs: undefined,
  sm: undefined,
  md: undefined,
  lg: undefined,
  xl: undefined
};

var _excluded$3 = ["ariaLabel", "className", "icon", "id", "invalid", "label", "onChange", "readonly"];

var PhoneInput = function PhoneInput(props) {
  var _classNames;

  var ariaLabel = props.ariaLabel,
      className = props.className,
      icon = props.icon,
      id = props.id,
      invalid = props.invalid,
      label = props.label,
      onChange = props.onChange,
      readonly = props.readonly,
      attributes = _objectWithoutProperties(props, _excluded$3);

  var _useState = useState(!invalid),
      _useState2 = _slicedToArray(_useState, 2),
      isValid = _useState2[0],
      setIsValid = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      value = _useState4[0],
      setValue = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      formatted = _useState6[0],
      setFormatted = _useState6[1];

  var validatePhoneNumber = function validatePhoneNumber() {
    var strict = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (!isPossiblePhoneNumber(value, 'US')) {
      setIsValid(strict ? false : value.length < 10);
    } else {
      setValue(formatted);
      setIsValid(isValidPhoneNumber(formatted, 'US'));
    }
  };

  useEffect(function () {
    validatePhoneNumber();
  }, [value, formatted]);

  var handleChange = function handleChange(e) {
    // Before there are 10 characters, do nothing
    var inputValue = new AsYouType('US').input("".concat(e.target.value)); // convert "(123) 456-7890" to "123-456-7890"

    var manValue = inputValue.replace('(', '').replace(')', '').replace(' ', '-');
    setFormatted(manValue);
    setValue(e.target.value);
    onChange(e);
  };

  var handleBlur = function handleBlur() {
    validatePhoneNumber(true);
  };

  var inputClassName = classNames((_classNames = {
    'input-phone': true,
    'form-control': true
  }, _defineProperty(_classNames, className, className), _defineProperty(_classNames, 'text-input__has-icon-left', !readonly && icon), _classNames));
  return /*#__PURE__*/React.createElement(React.Fragment, null, label && /*#__PURE__*/React.createElement(Label, {
    htmlFor: id
  }, label), /*#__PURE__*/React.createElement("div", {
    className: "input-group align-items-center mb-3"
  }, !readonly && icon && /*#__PURE__*/React.createElement("i", {
    className: "icon text-input__icon-left prism-icon-mobile"
  }), /*#__PURE__*/React.createElement(Input, _extends({}, attributes, {
    "aria-label": ariaLabel,
    className: inputClassName,
    id: id,
    invalid: !isValid,
    onBlur: handleBlur,
    onChange: handleChange,
    readonly: readonly,
    type: "phone",
    valid: false,
    value: value
  })), /*#__PURE__*/React.createElement(FormFeedback, {
    valid: isValid
  }, "This is not a valid 10-digit phone number.")));
};

PhoneInput.propTypes = {
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  icon: PropTypes.bool,
  id: PropTypes.string,
  invalid: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  readonly: PropTypes.bool
};
PhoneInput.defaultProps = {
  ariaLabel: 'phoneNumber',
  className: '',
  icon: true,
  id: v4(),
  invalid: false,
  label: undefined,
  onChange: function onChange() {},
  readonly: false
};
PhoneInput.displayName = 'PhoneInput';

var _excluded$2 = ["children", "className", "row", "disabled", "check", "inline", "tag", "readonly"];

var FormGroup = function FormGroup(props) {
  var children = props.children,
      className = props.className,
      row = props.row,
      disabled = props.disabled,
      check = props.check,
      inline = props.inline,
      Tag = props.tag,
      readonly = props.readonly,
      attributes = _objectWithoutProperties(props, _excluded$2);

  var classes = classNames(className, {
    'form-row': row,
    'form-check': check,
    'form-group': !check,
    'form-check-inline': check && inline,
    'align-items-center form-group-inline': inline,
    disabled: check && disabled
  }); // Add readonly prop to children if FormGroup is readonly

  var computedChildren = readonly ? React.Children.map(children, function (child) {
    var newChild = _objectSpread2({}, child);

    newChild.props = _objectSpread2(_objectSpread2({}, newChild.props), {}, {
      readonly: readonly
    });
    return newChild;
  }) : children;
  return /*#__PURE__*/React.createElement(Tag, _extends({}, attributes, {
    className: classes,
    readOnly: readonly
  }), computedChildren);
};

FormGroup.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  row: PropTypes.bool,
  check: PropTypes.bool,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  tag: PropTypes.string,
  className: PropTypes.string,
  readonly: PropTypes.bool
};
FormGroup.defaultProps = {
  tag: 'div',
  children: null,
  row: false,
  check: false,
  inline: false,
  disabled: false,
  className: '',
  readonly: false
};

var _excluded$1 = ["state", "static", "addon", "bsSize", "className", "iconAfter", "iconBefore", "iconLeft", "iconRight", "id", "inline", "innerRef", "invalid", "label", "name", "onClickAfter", "onClickBefore", "onClickLeft", "onClickRight", "plaintext", "readonly", "size", "tag", "type", "valid"];

var InteractiveInput = function InteractiveInput(props) {
  props.state;
      props.static;
      props.addon;
      props.bsSize;
      props.className;
      var iconAfter = props.iconAfter,
      iconBefore = props.iconBefore,
      iconLeft = props.iconLeft,
      iconRight = props.iconRight,
      id = props.id,
      inline = props.inline,
      innerRef = props.innerRef;
      props.invalid;
      var label = props.label,
      name = props.name,
      onClickAfter = props.onClickAfter,
      onClickBefore = props.onClickBefore,
      onClickLeft = props.onClickLeft,
      onClickRight = props.onClickRight;
      props.plaintext;
      var readonly = props.readonly;
      props.size;
      props.tag;
      var type = props.type;
      props.valid;
      var attributes = _objectWithoutProperties(props, _excluded$1);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showPassword = _useState2[0],
      setShowPassword = _useState2[1];

  var togglePasswordMask = function togglePasswordMask() {
    setShowPassword(!showPassword);
  };

  var _useInput = useInput(props),
      useInputClasses = _useInput.className;

  var inputClasses = classNames(useInputClasses, {
    'text-input__has-icon-left': !readonly && iconLeft,
    'text-input__has-icon-right': !readonly && (iconRight || type === 'password')
  });
  var passwordIconClasses = classNames({
    'text-input__icon-right': true,
    icon: true,
    'prism-icon-md': true,
    'password-toggle__icon': true,
    'prism-icon-eye icon-secondary': !showPassword,
    'prism-icon-eye-blocked text-charcoal': showPassword
  });
  var iconLeftClasses = classNames(_defineProperty({
    'text-input__icon-left': true,
    icon: true,
    'prism-icon-mobile': type === 'phone'
  }, iconLeft, type !== 'phone'));
  var iconRightClasses = classNames('text-input__icon-right', iconRight);
  var iconBeforeClasses = classNames(iconBefore);
  var iconAfterClasses = classNames(iconAfter);
  var labelClasses = classNames({
    'interactive-input__label': true,
    'icon-before': iconBefore && !inline
  });
  var inputType = type === 'password' && showPassword ? 'text' : type;
  return /*#__PURE__*/React.createElement(FormGroup, {
    inline: inline
  }, label && /*#__PURE__*/React.createElement(Label, {
    className: labelClasses,
    for: id
  }, label), /*#__PURE__*/React.createElement("span", {
    className: "d-flex align-items-center inline"
  }, !readonly && iconBefore && /*#__PURE__*/React.createElement(InputGroupAddon, {
    onClick: onClickBefore,
    addonType: "prepend"
  }, /*#__PURE__*/React.createElement("span", {
    className: iconBeforeClasses
  })), !readonly && iconLeft && /*#__PURE__*/React.createElement(Button, {
    icononly: true,
    className: "btn-input-icononly btn-input-icononly__left",
    onClick: onClickLeft
  }, /*#__PURE__*/React.createElement("span", {
    className: iconLeftClasses
  })), type === 'phone' ? /*#__PURE__*/React.createElement(PhoneInput, _extends({}, attributes, {
    id: id,
    name: name && name,
    className: inputClasses,
    readonly: readonly
  })) : /*#__PURE__*/React.createElement(Input, _extends({}, attributes, {
    id: id,
    name: name && name,
    ref: innerRef,
    className: inputClasses,
    readonly: readonly,
    type: inputType
  })), !readonly && iconRight && /*#__PURE__*/React.createElement(Button, {
    icononly: true,
    className: "btn-input-icononly btn-input-icononly__right",
    role: "button",
    onClick: onClickRight
  }, /*#__PURE__*/React.createElement("span", {
    className: iconRightClasses
  })), !readonly && type === 'password' && /*#__PURE__*/React.createElement(Button, {
    icononly: true,
    className: "btn-input-icononly btn-input-icononly__right",
    onClick: togglePasswordMask
  }, /*#__PURE__*/React.createElement("span", {
    className: passwordIconClasses
  })), !readonly && iconAfter && /*#__PURE__*/React.createElement(InputGroupAddon, {
    onClick: onClickAfter,
    addonType: "append",
    role: "option"
  }, /*#__PURE__*/React.createElement("span", {
    className: iconAfterClasses
  }))));
};

InteractiveInput.propTypes = {
  state: deprecated(PropTypes.string, 'Please use the props "valid" and "invalid" to indicate the state.'),
  static: deprecated(PropTypes.bool, 'Please use the prop "plaintext"'),
  addon: PropTypes.bool,
  bsSize: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  iconAfter: PropTypes.string,
  iconBefore: PropTypes.string,
  iconLeft: PropTypes.string,
  iconRight: PropTypes.string,
  id: PropTypes.string,
  inline: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.string]),
  invalid: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onClickAfter: PropTypes.func,
  onClickBefore: PropTypes.func,
  onClickLeft: PropTypes.func,
  onClickRight: PropTypes.func,
  plaintext: PropTypes.bool,
  readonly: PropTypes.bool,
  size: PropTypes.string,
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  type: PropTypes.string,
  valid: PropTypes.bool
};
InteractiveInput.defaultProps = {
  state: undefined,
  // deprecated
  static: undefined,
  // deprecated
  addon: false,
  bsSize: '',
  children: null,
  className: '',
  iconAfter: undefined,
  iconBefore: undefined,
  iconLeft: undefined,
  iconRight: undefined,
  id: v4(),
  inline: false,
  innerRef: null,
  invalid: undefined,
  label: undefined,
  name: undefined,
  onChange: function onChange() {},
  onClickAfter: function onClickAfter() {},
  onClickBefore: function onClickBefore() {},
  onClickLeft: function onClickLeft() {},
  onClickRight: function onClickRight() {},
  plaintext: false,
  readonly: false,
  size: '',
  tag: 'input',
  type: 'text',
  valid: undefined
};

var NumberInput = function NumberInput(props) {
  var className = props.className,
      id = props.id,
      max = props.max,
      min = props.min,
      onChange = props.onChange,
      readonly = props.readonly,
      initialValue = props.value;

  var _useState = useState(initialValue),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  var handleChange = function handleChange(newValue) {
    if (newValue >= min && newValue <= max) {
      setValue(newValue);
      onChange(newValue);
    }
  };

  var handleDecrementClick = function handleDecrementClick(e) {
    e.preventDefault();
    handleChange(value - 1);
  };

  var handleIncrementClick = function handleIncrementClick(e) {
    e.preventDefault();
    handleChange(value + 1);
  };

  var handleInputChange = function handleInputChange(e) {
    e.preventDefault();
    handleChange(Number(e.target.value));
  };

  var classes = classNames(className, 'input-group number-input__group', readonly && 'is-readonly');
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, !readonly && /*#__PURE__*/React.createElement("div", {
    className: "input-group-prepend"
  }, /*#__PURE__*/React.createElement(Button, {
    "data-test": "decrement-button",
    "aria-label": "decrement-button",
    disabled: value <= min,
    color: "secondary",
    onClick: handleDecrementClick,
    className: "number-input__decrement",
    id: "".concat(id, "-decrement")
  }, /*#__PURE__*/React.createElement("i", {
    className: "icon prism-icon-minus3"
  }))), /*#__PURE__*/React.createElement(Input, {
    onChange: handleInputChange,
    type: "text",
    className: "form-control",
    id: id,
    value: value,
    placeholder: "0",
    readonly: readonly,
    "aria-label": "number input",
    "aria-describedby": "basic-addon1"
  }), !readonly && /*#__PURE__*/React.createElement("div", {
    className: "input-group-append"
  }, /*#__PURE__*/React.createElement(Button, {
    "data-test": "increment-button",
    "aria-label": "increment-button",
    disabled: value >= max,
    color: "secondary",
    onClick: handleIncrementClick,
    className: "number-input__increment",
    id: "".concat(id, "-increment")
  }, /*#__PURE__*/React.createElement("i", {
    className: "icon prism-icon-plus"
  }))));
};

NumberInput.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
  readonly: PropTypes.bool,
  value: PropTypes.number
};
NumberInput.defaultProps = {
  className: '',
  id: v4(),
  max: 5,
  min: 0,
  onChange: function onChange() {},
  readonly: false,
  value: 0
};

var PasswordInput = function PasswordInput(props) {
  var readonly = props.readonly;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showPassword = _useState2[0],
      setShowPassword = _useState2[1];

  var togglePasswordMask = function togglePasswordMask() {
    setShowPassword(!showPassword);
  };

  var iconClassNames = classNames('text-input__icon-right', 'prism-icon', 'icon', 'prism-icon-md', 'password-toggle__icon', {
    'prism-icon-eye-blocked text-charcoal': showPassword,
    'prism-icon-eye icon-secondary': !showPassword
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center inline"
  }, /*#__PURE__*/React.createElement(Input, _extends({}, props, {
    type: showPassword ? 'text' : 'password',
    className: "font-size-md form-control text-input__has-icon-right",
    placeholder: "Password"
  })), !readonly && /*#__PURE__*/React.createElement(Button, {
    icononly: true,
    className: "btn-input-icononly btn-input-icononly__right",
    onClick: togglePasswordMask
  }, /*#__PURE__*/React.createElement("span", {
    className: iconClassNames,
    name: showPassword ? 'eye' : 'eye-blocked'
  })));
};

PasswordInput.propTypes = {
  readonly: PropTypes.bool
};
PasswordInput.defaultProps = {
  readonly: false
};

var _excluded = ["className", "initialValue", "label", "maxLength", "onChange", "readonly"];

var TextCounter = function TextCounter(props) {
  var className = props.className,
      initialValue = props.initialValue,
      label = props.label,
      maxLength = props.maxLength,
      onChange = props.onChange,
      readonly = props.readonly,
      attributes = _objectWithoutProperties(props, _excluded);

  var _React$useState = React.useState(initialValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      value = _React$useState2[0],
      setValue = _React$useState2[1];

  var remaining = maxLength - value.length;

  var handleChange = function handleChange(e) {
    setValue(e.target.value);
    onChange(e);
  };

  var counterClassName = classNames('form-text mt-8 textarea-counter__count', {
    'text-danger': remaining < 0
  });

  var inputProps = _objectSpread2(_objectSpread2({}, attributes), {}, {
    className: classNames('form-control text-area__textarea', className),
    defaultValue: value,
    onChange: handleChange,
    readonly: readonly,
    type: 'textarea'
  });

  return /*#__PURE__*/React.createElement("div", {
    className: "textarea-counter"
  }, label && /*#__PURE__*/React.createElement(Label, null, label), /*#__PURE__*/React.createElement(Input, inputProps), !readonly && /*#__PURE__*/React.createElement("small", {
    id: "textCounter",
    className: counterClassName
  }, remaining, ' ', "characters remaining"));
};

TextCounter.propTypes = {
  /**
   * Optionally include additional classnames to be appended after default ones.
   */
  className: PropTypes.string,

  /**
   * Optionally pass a specific ID. Otherwise a UUID is generated.
   */
  id: PropTypes.string,

  /**
   * Optionally set a starting value. Unlike placeholder, this is applied to any character limits.
   */
  initialValue: PropTypes.string,

  /**
   * Optionally apply a form label
   */
  label: PropTypes.string,

  /**
   * The maximum number of characters allowed in the input.
   * Exceeding this number will yield a visual warning. Defaults to 120.
   */
  maxLength: PropTypes.number,

  /**
   * Optionally include a callback function to be called when updating the current value.
   */
  onChange: PropTypes.func,

  /**
   * Optionally set a placeholder other than the default.
   */
  placeholder: PropTypes.string,

  /**
   * Optionally set input as readonly - hides character remaining count
   */
  readonly: PropTypes.bool,

  /**
   * Textarea HTML attributes, how many rows of text tall is the input?
   */
  rows: PropTypes.string,

  /**
   * Optionally override the input type. Defaults to textarea.
   */
  type: PropTypes.string
};
TextCounter.defaultProps = {
  className: '',
  id: v4(),
  initialValue: '',
  label: undefined,
  maxLength: 120,
  onChange: function onChange() {},
  placeholder: 'Enter some text',
  readonly: false,
  rows: '5',
  type: 'textarea'
};
TextCounter.displayName = 'TextCounter';

Input.Interactive = InteractiveInput;
Input.Label = Label;
Input.Number = NumberInput;
Input.NumberInput = NumberInput;
Input.Password = PasswordInput;
Input.Phone = PhoneInput;
Input.Text = Input;
Input.TextCounter = TextCounter;

export { InteractiveInput, Label, NumberInput, PasswordInput, PhoneInput, TextCounter, Input as default };
//# sourceMappingURL=index.js.map
